/**
 * useStripeCheckout hook
 *
 * Manages redirects to the Stripe customer portal. Per-site checkout flows
 * (publish/upgrade/republish) live in `useSiteActions` and the `billingApi`
 * helpers — they always return a `checkoutUrl` directly, no extra hook needed.
 *
 * Both portal openers launch a new browser tab so the user can close it when
 * done and return to the original app tab with state preserved (TanStack's
 * `refetchOnWindowFocus` then refreshes the list automatically). The new tab
 * is opened synchronously inside the click handler — required to keep the
 * user-gesture context alive across the async session-creation roundtrip,
 * otherwise popup blockers reject it. If the blank tab can't be opened
 * (popup blocker), we fall back to a same-tab redirect.
 */

import { useState, useCallback } from 'react';
import * as billingApi from '../api/billingApi';
import type { BillingPortalSession } from '../types/billing';

export interface UseStripeCheckoutReturn {
  loading: boolean;
  error: string | null;
  openBillingPortal: () => Promise<boolean>;
  openSiteBillingPortal: (siteId: string) => Promise<boolean>;
}

function navigateToPortal(tab: Window | null, url: string): void {
  if (tab && !tab.closed) {
    tab.location.href = url;
    tab.focus();
  } else {
    window.location.href = url;
  }
}

export function useStripeCheckout(): UseStripeCheckoutReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Shared open-portal flow; only the session-creation call differs between
  // the org-wide and per-site portals.
  const openPortal = useCallback(
    async (createSession: () => Promise<BillingPortalSession>): Promise<boolean> => {
      const tab = window.open('about:blank', '_blank');
      setLoading(true);
      setError(null);

      try {
        const session = await createSession();
        navigateToPortal(tab, session.url);
        setLoading(false);
        return true;
      } catch (err) {
        if (tab && !tab.closed) tab.close();
        const message = err instanceof Error ? err.message : 'Failed to open billing portal';
        setError(message);
        setLoading(false);
        return false;
      }
    },
    [],
  );

  const openBillingPortal = useCallback(
    (): Promise<boolean> => openPortal(() => billingApi.createBillingPortalSession()),
    [openPortal],
  );

  const openSiteBillingPortal = useCallback(
    (siteId: string): Promise<boolean> =>
      openPortal(() => billingApi.createSiteBillingPortalSession(siteId)),
    [openPortal],
  );

  return {
    loading,
    error,
    openBillingPortal,
    openSiteBillingPortal,
  };
}
